/**
 * Stock Message Component - Handles contextual stock message updates
 * Updates stock messages based on cart contents and quantity selector changes
 */

class StockMessageComponent {
  constructor() {
    this.debug = window.themeSettings?.dispatch_debug_mode || false;
    this.stockMessages = document.querySelectorAll('[data-stock-message]');
    this.quantityInputs = document.querySelectorAll('quantity-selector-component input[name="quantity"]');
    this.variantSelectors = document.querySelectorAll('variant-selector-component');
    
    // Add delay mechanism to prevent flickering during cart updates
    this.cartUpdateInProgress = false;
    this.quantityChangeTimeout = null;
    
    if (this.stockMessages.length > 0) {
      this.init();
    }
  }

  init() {
    this.setupEventListeners();
    this.updateAllStockMessages();
    
    if (this.debug) {
      console.log('Stock Message Component initialized', {
        stockMessages: this.stockMessages.length,
        quantityInputs: this.quantityInputs.length
      });
    }
  }

  setupEventListeners() {
    // Listen for cart changes
    window.addEventListener('cart:updated', () => {
      this.cartUpdateInProgress = true;
      // Small delay to ensure cart data is fully updated
      setTimeout(() => {
        this.updateAllStockMessages();
        // Clear the flag after update completes
        setTimeout(() => {
          this.cartUpdateInProgress = false;
        }, 200);
      }, 100);
    });

    window.addEventListener('cart:item-added', () => {
      this.cartUpdateInProgress = true;
      // Small delay to ensure cart data is fully updated
      setTimeout(() => {
        this.updateAllStockMessages();
        // Clear the flag after update completes
        setTimeout(() => {
          this.cartUpdateInProgress = false;
        }, 200);
      }, 100);
    });

    window.addEventListener('cart:emptied', () => {
      this.updateAllStockMessages();
    });

    // Listen for variant changes
    window.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event);
    });

    // Listen for quantity changes
    this.quantityInputs.forEach(input => {
      input.addEventListener('input', (event) => {
        this.handleQuantityChange(event);
      });
      
      input.addEventListener('change', (event) => {
        this.handleQuantityChange(event);
      });
    });

    // Listen for section reloads
    document.addEventListener('shopify:section:load', () => {
      this.reinitialize();
    });

    if (this.debug) {
      console.log('Stock message event listeners setup complete');
    }
  }

  reinitialize() {
    this.stockMessages = document.querySelectorAll('[data-stock-message]');
    this.quantityInputs = document.querySelectorAll('quantity-selector-component input[name="quantity"]');
    this.variantSelectors = document.querySelectorAll('variant-selector-component');
    
    console.log('Reinitializing stock message component:', {
      stockMessages: this.stockMessages.length,
      quantityInputs: this.quantityInputs.length
    });
    
    this.setupEventListeners();
    this.updateAllStockMessages();
  }

  handleVariantChange(event) {
    const variant = event.detail.variant;
    if (!variant) return;

    // Update stock messages for this product
    this.stockMessages.forEach(stockMessage => {
      const productId = parseInt(stockMessage.dataset.productId);
      if (productId === variant.product_id) {
        this.updateStockMessageForVariant(stockMessage, variant);
      }
    });
  }

  handleQuantityChange(event) {
    // Don't update during cart operations to prevent flickering
    if (this.cartUpdateInProgress) {
      return;
    }

    const quantity = parseInt(event.target.value) || 1;
    const form = event.target.closest('form');
    
    // Clear any existing timeout
    if (this.quantityChangeTimeout) {
      clearTimeout(this.quantityChangeTimeout);
    }
    
    // Debounce quantity changes to prevent rapid updates
    this.quantityChangeTimeout = setTimeout(() => {
      if (form) {
        const variantIdInput = form.querySelector('input[name="id"]');
        if (variantIdInput) {
          const variantId = parseInt(variantIdInput.value);
          this.updateStockMessagesForQuantity(variantId, quantity);
        }
      }
    }, 150); // Small delay to debounce rapid changes
  }

  async updateAllStockMessages() {
    try {
      // Fetch current cart data
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      

      this.stockMessages.forEach(stockMessage => {
        this.updateStockMessage(stockMessage, cart);
      });

      if (this.debug) {
        console.log('All stock messages updated', { cartItemCount: cart.item_count });
      }
    } catch (error) {
      console.error('Error updating stock messages:', error);
    }
  }

  updateStockMessagesForQuantity(variantId, intendedQuantity) {
    this.stockMessages.forEach(stockMessage => {
      const messageVariantId = parseInt(stockMessage.dataset.variantId);
      if (messageVariantId === variantId) {
        this.updateStockMessageWithQuantity(stockMessage, intendedQuantity);
      }
    });
  }

  updateStockMessageForVariant(stockMessage, variant) {
    // Update data attributes with new variant info
    stockMessage.dataset.variantId = variant.id;
    stockMessage.dataset.inventoryQuantity = variant.inventory_quantity || 0;
    stockMessage.dataset.inventoryPolicy = variant.inventory_policy || 'deny';

    // Recalculate and update
    this.updateStockMessage(stockMessage);
  }

  updateStockMessage(stockMessage, cart = null) {
    const variantId = parseInt(stockMessage.dataset.variantId);
    const inventoryQuantity = parseInt(stockMessage.dataset.inventoryQuantity) || 0;
    const inventoryPolicy = stockMessage.dataset.inventoryPolicy;
    const releaseDate = stockMessage.dataset.releaseDate;
    const customStockMessage = stockMessage.dataset.stockMessageText;
    const showStockLevel = stockMessage.dataset.showStockLevel === 'true';

    // Calculate cart quantity for this variant
    let cartQuantity = 0;
    if (cart && cart.items) {
      cart.items.forEach(item => {
        if (item.variant_id === variantId) {
          cartQuantity += item.quantity;
        }
      });
    } else if (window.__cart && window.__cart.items) {
      window.__cart.items.forEach(item => {
        if (item.variant_id === variantId) {
          cartQuantity += item.quantity;
        }
      });
    }

    // Calculate effective stock after cart contents
    const effectiveStock = inventoryQuantity - cartQuantity;

    this.updateStockMessageDisplay(stockMessage, {
      effectiveStock,
      cartQuantity,
      inventoryQuantity,
      inventoryPolicy,
      releaseDate,
      customStockMessage,
      showStockLevel
    });
  }

  updateStockMessageWithQuantity(stockMessage, intendedQuantity) {
    const variantId = parseInt(stockMessage.dataset.variantId);
    const inventoryQuantity = parseInt(stockMessage.dataset.inventoryQuantity) || 0;
    const inventoryPolicy = stockMessage.dataset.inventoryPolicy;
    const releaseDate = stockMessage.dataset.releaseDate;
    const customStockMessage = stockMessage.dataset.stockMessageText;
    const showStockLevel = stockMessage.dataset.showStockLevel === 'true';

    // Calculate cart quantity for this variant
    let cartQuantity = 0;
    if (window.__cart && window.__cart.items) {
      window.__cart.items.forEach(item => {
        if (item.variant_id === variantId) {
          cartQuantity += item.quantity;
        }
      });
    }

    // Calculate effective stock after cart contents (before considering intended quantity)
    const stockAfterCart = inventoryQuantity - cartQuantity;
    
    // Check if intended quantity would exceed available stock
    const wouldExceedStock = intendedQuantity > stockAfterCart;

    this.updateStockMessageDisplay(stockMessage, {
      effectiveStock: stockAfterCart,
      wouldExceedStock,
      cartQuantity,
      intendedQuantity,
      inventoryQuantity,
      inventoryPolicy,
      releaseDate,
      customStockMessage,
      showStockLevel
    });
  }

  updateStockMessageDisplay(stockMessage, data) {
    const {
      effectiveStock,
      wouldExceedStock,
      inventoryPolicy,
      releaseDate,
      customStockMessage,
      showStockLevel
    } = data;

    let message = '';
    let hasReleaseDateInFuture = false;

    // Check if release date exists and is in the future
    if (releaseDate && releaseDate !== 'null' && releaseDate !== '') {
      const releaseDateObj = this.parseReleaseDateForComparison(releaseDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to start of day for comparison
      
      if (releaseDateObj && releaseDateObj >= today) {
        hasReleaseDateInFuture = true;
      }
    }
    // Priority 1: Check if intended quantity would exceed stock (for quantity selector previews)
    if (wouldExceedStock !== undefined) {
      if (wouldExceedStock) {
        // Intended quantity exceeds available stock
        // Check if we have a future release date - if so, use that
        if (hasReleaseDateInFuture) {
          const formattedDate = this.formatReleaseDate(releaseDate);
          message = stockMessage.dataset.availableFromMessage.replace('{{ date }}', formattedDate);
        } else if (inventoryPolicy === 'continue') {
          if (customStockMessage) {
            message = customStockMessage;
          } else {
            // Build message from default dispatch settings
            const minDays = parseInt(window.themeSettings?.dispatch_default_min_days) || 1;
            const maxDays = parseInt(window.themeSettings?.dispatch_default_max_days) || 3;
            
            message = this.buildDispatchMessage(minDays, maxDays);
          }
        } else {
          message = stockMessage.dataset.outOfStockMessage;
        }
      } else {
        // Intended quantity is within available stock
        // Stock takes priority - show stock message
        // Use custom stock message only if showing stock level, otherwise use default "in stock" message
        if (showStockLevel && customStockMessage && customStockMessage !== '') {
          message = customStockMessage;
          if (effectiveStock > 0) {
            message += ` | ${effectiveStock} available`;
          }
        } else {
          message = stockMessage.dataset.inStockMessage;
        }
      }
    }
    // Priority 2: Check effective stock levels (for cart-based updates)
    else if (effectiveStock > 0) {
      // Still have stock available after cart contents
      // Stock takes priority - show stock message
      // Use custom stock message only if showing stock level, otherwise use default "in stock" message
      if (showStockLevel && customStockMessage && customStockMessage !== '') {
        message = customStockMessage;
        message += ` | ${effectiveStock} available`;
      } else {
        message = stockMessage.dataset.inStockMessage;
      }
    }
    else if (inventoryPolicy === 'continue') {
      // Out of effective stock but continue selling
      // Check if we have a future release date - if so, use that
      if (hasReleaseDateInFuture) {
        const formattedDate = this.formatReleaseDate(releaseDate);
        message = stockMessage.dataset.availableFromMessage.replace('{{ date }}', formattedDate);
      } else if (customStockMessage) {
        message = customStockMessage;
      } else {
        // Build message from default dispatch settings
        const minDays = parseInt(window.themeSettings?.dispatch_default_min_days) || 1;
        const maxDays = parseInt(window.themeSettings?.dispatch_default_max_days) || 3;
        
        message = this.buildDispatchMessage(minDays, maxDays);
      }
    }
    else {
      // Out of effective stock and not continuing
      // Check if we have a future release date - if so, use that
      if (hasReleaseDateInFuture) {
        const formattedDate = this.formatReleaseDate(releaseDate);
        message = stockMessage.dataset.availableFromMessage.replace('{{ date }}', formattedDate);
      } else {
        message = stockMessage.dataset.outOfStockMessage;
      }
    }

    // Update the display
    const textElement = stockMessage.querySelector('[data-stock-message-text]');
    
    if (textElement && message) {
      textElement.textContent = message;
    }

    if (this.debug) {
      console.log('Stock message updated:', {
        variantId: stockMessage.dataset.variantId,
        effectiveStock,
        wouldExceedStock,
        message,
        data
      });
    }
  }

  getIntendedQuantity(variantId) {
    // Find quantity input for this variant
    const form = document.querySelector(`form input[name="id"][value="${variantId}"]`)?.closest('form');
    if (form) {
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput) {
        return parseInt(quantityInput.value) || 1;
      }
    }
    return 1; // Default quantity
  }

  formatReleaseDate(dateString) {
    try {
      if (dateString.includes('-')) {
        // ISO format (YYYY-MM-DD)
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } else if (dateString.includes('/')) {
        // Already in dd/mm/yyyy format
        return dateString;
      }
      return dateString;
    } catch (error) {
      console.warn('Error formatting release date:', dateString, error);
      return dateString;
    }
  }

  parseReleaseDateForComparison(dateString) {
    try {
      if (dateString.includes('-')) {
        // ISO format (YYYY-MM-DD)
        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0);
        return date;
      } else if (dateString.includes('/')) {
        // dd/mm/yyyy format
        const [day, month, year] = dateString.split('/').map(Number);
        const date = new Date(year, month - 1, day);
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return null;
    } catch (error) {
      console.warn('Error parsing release date for comparison:', dateString, error);
      return null;
    }
  }

  buildDispatchMessage(minDays, maxDays) {
    if (minDays === maxDays) {
      if (minDays === 1) {
        return `Dispatches within ${minDays} working day`;
      } else {
        return `Dispatches within ${minDays} working days`;
      }
    } else {
      return `Dispatches within ${minDays}-${maxDays} working days`;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.stockMessageComponent = new StockMessageComponent();
});

// Also initialize on page transitions
document.addEventListener('pageTransitionComplete', () => {
  if (window.stockMessageComponent) {
    window.stockMessageComponent.reinitialize();
  } else {
    window.stockMessageComponent = new StockMessageComponent();
  }
});
